import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit8Notes: UnitNotes = {
  unitId: "unit-8",
  unitName: "Unit 8: Ecology",
  examWeight: "10–15%",
  focus: "Evolution, Energetics, Information Storage, and Systems Interactions",
  sections: [
    {
      id: "topic-8-1",
      title: "Topic 8.1: Responses to the Environment",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧠 Behavioral Mechanisms</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Organisms respond to changes in their internal and external environments through behavioral and physiological mechanisms shaped by natural selection to increase <strong>evolutionary fitness</strong>.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔄 Taxis</h4>
                <p className="text-sm text-slate-700 mt-1">Directional movement toward (+) or away (-) from a stimulus.</p>
                <p className="text-xs text-slate-500 mt-2 italic">Example: Chemotaxis in bacteria moving toward sugar; Phototaxis in moths moving toward light.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🌀 Kinesis</h4>
                <p className="text-sm text-slate-700 mt-1">Non-directional change in activity or turning rate. Organism moves faster/slower but not toward the stimulus.</p>
                <p className="text-xs text-slate-500 mt-2 italic">Example: Woodlice moving rapidly in dry areas to find humidity.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌿 Plant Responses</h3>
            <div className="space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">☀️ Phototropism</h4>
                <p className="text-sm text-slate-700 mt-1">Growth toward/away from light. Mediated by <strong>auxin</strong>, which accumulates on the <em>shaded</em> side of the stem, causing cell elongation and bending the plant toward the light.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">🌑 Photoperiodism</h4>
                <p className="text-sm text-slate-700 mt-1">Physiological response to the relative length of night and day. Plants use <strong>phytochromes</strong> to detect night length.</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-700 space-y-1">
                  <li><strong>Short-day plants:</strong> Flower when the night exceeds a critical length (late summer/fall).</li>
                  <li><strong>Long-day plants:</strong> Flower when the night is shorter than a critical length (spring/summer).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🐝 Communication & Cooperation</h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <KeyTerm term="Signaling Behaviors" definition="Information exchange via visual, audible, tactile, electrical, or chemical signals (e.g., pheromones)." />
                <KeyTerm term="Waggle Dance" definition="Honeybees communicate distance and direction of food; the angle relative to the hive's vertical axis corresponds to the angle relative to the sun." />
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🤝 Cooperative Behaviors</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-2">
                  <li><strong>Pack Hunting:</strong> Wolves take down larger prey than solitary hunters.</li>
                  <li><strong>Herd Behavior:</strong> Grouping to confuse predators and reduce individual risk.</li>
                  <li><strong>Altruism & Kin Selection:</strong> Reducing own fitness to help relatives, driven by preserving shared genes.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-2",
      title: "Topic 8.2: Energy Flow Through Ecosystems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔥 Metabolic Strategies</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🌡️ Endotherms (Regulators)</h4>
                <p className="text-xs text-slate-700 mt-1">Use thermal energy from metabolism for homeostasis.</p>
                <div className="mt-2 text-xs">
                  <span className="text-green-700 font-bold">Pros:</span> Active in cold. <br/>
                  <span className="text-red-700 font-bold">Cons:</span> High energy cost; frequent eating.
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">☀️ Ectotherms (Conformers)</h4>
                <p className="text-xs text-slate-700 mt-1">Rely on external heat sources.</p>
                <div className="mt-2 text-xs">
                  <span className="text-green-700 font-bold">Pros:</span> Low metabolic rate; less food. <br/>
                  <span className="text-red-700 font-bold">Cons:</span> Activity limited by environmental temp.
                </div>
              </div>
            </div>
            <ImportantNote>
              <strong>Size vs. Rate:</strong> Smaller organisms have a <strong>higher</strong> metabolic rate per unit of mass than larger organisms (e.g., a mouse eats more relative to its weight than an elephant).
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🥗 Trophic Levels</h3>
            <div className="space-y-3">
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Autotrophs (Producers)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Photosynthetic:</strong> Sunlight (Plants/Algae). <strong>Chemosynthetic:</strong> Inorganic molecules like <InlineMath math="H_2S" /> (Deep-sea vent bacteria).</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Heterotrophs (Consumers)</h4>
                <p className="text-sm text-slate-700 mt-1">Primary (Herbivores), Secondary/Tertiary (Carnivores), and <strong>Decomposers (Detritivores)</strong> who recycle dead matter.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-300">
                <h4 className="font-bold text-slate-900">The 10% Rule</h4>
                <p className="text-sm text-slate-700 mt-1">Only ~10% of energy transfers to the next level. 90% is lost as <strong>heat (entropy)</strong> or remains in indigestible material.</p>
                <p className="text-xs text-slate-500 mt-2 italic">This limits trophic levels to 4-5 and results in smaller populations of top predators.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-3",
      title: "Topic 8.3: Population Ecology",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Growth Dynamics & Models</h3>
            <p className="text-sm text-slate-700 mb-4">Population size (<InlineMath math="N" />) changes: <InlineMath math="\text{Change} = (B + I) - (D + E)" /></p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Exponential Growth (J-curve)</h4>
                <p className="text-xs text-slate-600 mb-2">Ideal conditions, unlimited resources.</p>
                <BlockMath math="\frac{dN}{dt} = r_{max}N" />
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Logistic Growth (S-curve)</h4>
                <p className="text-xs text-slate-600 mb-2">Slows as it reaches Carrying Capacity (<InlineMath math="K" />).</p>
                <BlockMath math="\frac{dN}{dt} = r_{max}N \left( \frac{K-N}{K} \right)" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Reproductive Strategies</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg bg-orange-50 border-orange-200">
                <h4 className="font-bold text-orange-900">r-selected</h4>
                <ul className="text-xs list-disc pl-4 mt-2 space-y-1">
                  <li>Early/abundant reproduction</li>
                  <li>Short life, low parental care</li>
                  <li>Unstable environments</li>
                  <li><em>Examples: Bacteria, weeds, insects</em></li>
                </ul>
              </div>
              <div className="p-4 border rounded-lg bg-indigo-50 border-indigo-200">
                <h4 className="font-bold text-indigo-900">K-selected</h4>
                <ul className="text-xs list-disc pl-4 mt-2 space-y-1">
                  <li>Late reproduction, few offspring</li>
                  <li>Long life, high parental care</li>
                  <li>Stable environments (at K)</li>
                  <li><em>Examples: Humans, elephants</em></li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-4",
      title: "Topic 8.4: Effect of Density on Populations",
      content: (
        <div className="space-y-6">
          <section>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Density-Dependent</h4>
                <p className="text-xs text-slate-600 mb-2">Regulate population around K.</p>
                <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                  <li><strong>Competition:</strong> Food, space, light.</li>
                  <li><strong>Predation:</strong> Higher density makes prey easier to find.</li>
                  <li><strong>Disease:</strong> Pathogens spread faster.</li>
                  <li><strong>Waste:</strong> Toxic buildup (e.g., yeast/alcohol).</li>
                </ul>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Density-Independent</h4>
                <p className="text-xs text-slate-600 mb-2">Affect size regardless of density.</p>
                <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                  <li><strong>Natural Disasters:</strong> Floods, fires, volcanoes.</li>
                  <li><strong>Anthropogenic:</strong> Pollution, habitat destruction.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-5",
      title: "Topic 8.5: Community Ecology",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Species Diversity</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm term="Species Richness" definition="The total number of different species in a community." />
              <KeyTerm term="Species Evenness" definition="Relative abundance; the proportion of each species to the total." />
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="font-bold text-indigo-900 mb-2">Simpson’s Diversity Index</h4>
              <BlockMath math="\text{Diversity Index} = 1 - \sum \left( \frac{n}{N} \right)^2" />
              <p className="text-xs text-slate-600 mt-2">High Index = High Biodiversity (resilient). Low Index = Low Biodiversity (fragile).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Interspecific Interactions</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900 text-sm">Competition (-/-)</h4>
                <p className="text-xs text-slate-700"><strong>Competitive Exclusion:</strong> Two species can't occupy the same niche indefinitely. Results in extinction or <strong>resource partitioning</strong>.</p>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <h4 className="font-bold text-green-900 text-sm">Mutualism (+/+)</h4>
                  <p className="text-xs text-slate-700 mt-1">Both benefit (Bees/flowers).</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-900 text-sm">Commensalism (+/0)</h4>
                  <p className="text-xs text-slate-700 mt-1">One benefits (Barnacles/whale).</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <h4 className="font-bold text-orange-900 text-sm">Parasitism (+/-)</h4>
                  <p className="text-xs text-slate-700 mt-1">Host is harmed (Ticks).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-6",
      title: "Topic 8.6: Biodiversity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💎 Critical Species</h3>
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-amber-900">Keystone Species</h4>
                <p className="text-sm text-slate-700 mt-1">Disproportionate effect relative to abundance.</p>
                <ul className="text-xs list-disc pl-5 mt-2 space-y-1">
                  <li><strong>Sea Otters:</strong> Control urchins to save kelp forests (Trophic Cascade).</li>
                  <li><strong>Wolves:</strong> Reintroduction in Yellowstone altered elk behavior and river flow.</li>
                </ul>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-300">
                <h4 className="font-bold text-slate-900">Ecosystem Engineers</h4>
                <p className="text-sm text-slate-700 mt-1">Organisms that create or modify physical habitats. <strong>Example:</strong> Beavers building dams create ponds for fish/amphibians.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-7",
      title: "Topic 8.7: Disruptions in Ecosystems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Environmental Threats</h3>
            <div className="space-y-3">
              <KeyTerm term="Invasive Species" definition="Non-native species lacking predators; outcompete natives (e.g., Kudzu, Zebra mussels, Cane toads)." />
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🌊 Eutrophication Steps</h4>
                <ol className="list-decimal pl-5 text-xs text-slate-700 mt-2 space-y-1">
                  <li>Excess N/P runoff enters water.</li>
                  <li><strong>Algal Bloom</strong> occurs.</li>
                  <li>Algae die; bacteria decompose them.</li>
                  <li>Bacteria consume all oxygen (<strong>Hypoxia</strong>).</li>
                  <li>Fish die (Dead Zone).</li>
                </ol>
              </div>
              <KeyTerm term="Biomagnification" definition="Toxins (DDT/Mercury) increase in concentration up the food chain; top predators most affected." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Ecological Succession</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Primary</h4>
                <p className="text-xs text-slate-600 italic">No soil exists (lava/glacier).</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Pioneer Species:</strong> Lichens and mosses break rock into soil.</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Secondary</h4>
                <p className="text-xs text-slate-600 italic">Soil remains (fire/farm).</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Pioneer Species:</strong> Grasses and weeds. Faster than primary.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit8Notes;